import { Component, OnInit, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { SidebarFloatingComponent, SidebarNavItem, SidebarNavSection, SidebarUser, SidebarAppIcon } from './sidebar/sidebar-floating.component';
import { ThemeService } from '../services/theme.service';
import { AuthService } from '../core/auth/auth.service';
import { PermissionService } from '../core/auth/permission.service';
import { ROUTE_PERMS } from '../core/auth/security-domains';
import { SessionWarningDialogComponent } from '../shared/components/session-warning-dialog.component';
import { Apollo, gql } from 'apollo-angular';
import { Subject, interval, of } from 'rxjs';
import { takeUntil, switchMap, catchError, retry } from 'rxjs/operators';
import { GotifyService } from '../core/services/gotify.service';
import { PushService } from '../core/services/push.service';
import { AlertsBadgeService } from '../core/services/alerts-badge.service';
import { FocusModeService } from '../core/services/focus-mode.service';
import { NombreLocalService } from '../core/services/nombre-local.service';
import { TranslateService } from '@ngx-translate/core';
import { nombreVisible, inicialesVisibles } from '../shared/utils/nombre-visible';
import { avatarGradientForSeed, avatarPicture } from '../shared/utils/avatar';
import { TokenUsageService, UsageViewModel } from '../features/settings/usage/token-usage.service';
import { ExportarBandejaComponent } from '../features/documentos/exportar-bandeja.component';
import { KnowledgeReviewWatchService, ReviewNotice } from '../features/knowledge/services/knowledge-review-watch.service';
import { KnowledgeReviewDialogComponent, ReviewOutcome } from '../features/knowledge/components/knowledge-review-dialog/knowledge-review-dialog.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SidebarFloatingComponent,
    SessionWarningDialogComponent,
    ExportarBandejaComponent,
    KnowledgeReviewDialogComponent,
    TranslatePipe
  ],
  template: `
    <div class="synapse-layout" [attr.data-theme]="isDarkMode ? 'dark' : 'light'">
      <app-sidebar-floating
        *ngIf="isAuthenticated && !focusMode.active()"
        [appIcon]="appIcon"
        [sections]="sidebarSections"
        [navItems]="sidebarNavItems"
        [user]="currentUser"
        (collapsedChange)="onSidebarCollapsed($event)"
      ></app-sidebar-floating>

      <div class="synapse-main-content"
           [class.synapse-main-content--collapsed]="isSidebarCollapsed"
           [class.synapse-main-content--focus]="focusMode.active()">
        <!-- Avisos globales flotantes: fuera del flujo para no sumar altura a páginas de alto fijo -->
        <div class="synapse-banners">
        @if (showUsageBanner()) {
          <div class="synapse-usage-banner" role="alert">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ 'settings.usage.globalBanner' | translate:{ used: usageVm()?.usedLabel, max: usageVm()?.maxLabel } }}</span>
            <a class="synapse-usage-banner-action" [routerLink]="['/settings']" [queryParams]="{ section: 'uso' }">
              {{ 'settings.usage.globalBannerAction' | translate }}
            </a>
            <button type="button" class="synapse-usage-banner-dismiss" (click)="dismissUsageBanner()" aria-label="Cerrar">
              <i class="pi pi-times"></i>
            </button>
          </div>
        }
        <!-- Revisión previa de ficheros de conocimiento (SYN-899): una revisión lista o un
             procesado que falló. Sale del servidor, así que sobrevive a recargar la página. -->
        @if (reviewBanner(); as n) {
          <div class="synapse-usage-banner synapse-review-banner" [class.is-error]="n.kind === 'error'" role="status">
            <i class="pi" [class.pi-check-square]="n.kind === 'review'" [class.pi-exclamation-circle]="n.kind === 'error'"></i>
            <span>
              {{ (n.kind === 'review' ? 'knowledge.review.banner.ready' : 'knowledge.review.banner.error') | translate:{ name: n.file.filename } }}
              @if (n.kind === 'error' && n.file.statusDetail) { — {{ n.file.statusDetail }} }
              @if (reviewBannerMore() > 0) { · {{ 'knowledge.review.banner.more' | translate:{ n: reviewBannerMore() } }} }
            </span>
            @if (n.kind === 'review') {
              <button type="button" class="synapse-usage-banner-action" (click)="openReviewFromBanner(n)">
                {{ 'knowledge.review.banner.action' | translate }}
              </button>
            }
            <button type="button" class="synapse-usage-banner-dismiss" (click)="knowledgeWatch.dismiss(n)" aria-label="Cerrar">
              <i class="pi pi-times"></i>
            </button>
          </div>
        }
        </div>
        <router-outlet></router-outlet>
      </div>

      <app-session-warning-dialog></app-session-warning-dialog>
      <!-- Progreso y resultado de «Exportar conversación» (SYN-930), sobre cualquier pantalla -->
      <sg-exportar-bandeja></sg-exportar-bandeja>
      <!-- La revisión de un fichero se abre desde el aviso, el modal de ficheros o ?review= -->
      @if (knowledgeWatch.openReviewId(); as reviewId) {
        <app-knowledge-review-dialog
          [reviewId]="reviewId"
          (closed)="knowledgeWatch.closeReview()"
          (done)="onReviewDone($event)"
        ></app-knowledge-review-dialog>
      }
      @if (reviewToast(); as msg) {
        <div class="synapse-review-toast" role="status">{{ msg }}</div>
      }
    </div>
  `,
  styles: [`
    .synapse-layout {
      background-color: var(--color-light);
      transition: background-color 0.3s ease;
      position: relative;
    }

    .synapse-layout[data-theme="dark"] {
      background-color: #000000;
    }

    /* Sidebar plana pegada al borde (sin los 16px de margen flotante) */
    .synapse-main-content {
      margin-left: calc(248px + 24px);
      transition: margin-left 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .synapse-main-content--collapsed {
      margin-left: calc(72px + 24px);
    }

    /* Modo foco (Grafo): sin sidebar, el contenido a todo el ancho */
    .synapse-main-content.synapse-main-content--focus {
      margin-left: 0;
    }

    @media (max-width: 1019px) {
      .synapse-main-content.synapse-main-content--focus {
        padding-bottom: var(--sg-safe-bottom);
      }
    }

    @media (max-width: 1019px) {
      .synapse-layout {
        overflow-x: hidden;
      }
      .synapse-main-content {
        margin-left: 0;
        padding-top: var(--sg-safe-top);
        padding-bottom: calc(80px + var(--sg-safe-bottom));
        overflow-x: hidden;
      }
    }

    @media (max-width: 480px) {
      .synapse-main-content {
        margin-left: 0;
        padding-bottom: calc(72px + var(--sg-safe-bottom));
      }
    }

    /* Contenedor flotante de avisos: fuera del flujo (position: fixed), así no suma altura a
       las páginas de alto fijo (calc(100vh - …)) ni provoca el scroll general */
    .synapse-banners {
      position: fixed;
      top: calc(12px + var(--sg-safe-top, 0px));
      left: 50%;
      transform: translateX(-50%);
      z-index: 90;
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: min(760px, calc(100vw - 24px));
      pointer-events: none;
    }

    .synapse-banners:empty {
      display: none;
    }

    .synapse-banners > * {
      pointer-events: auto;
    }

    /* Aviso global de cap de tokens superado (informativo, no bloquea nada).
       Fondo opaco: flota sobre el contenido. El texto hace wrap, nunca se recorta */
    .synapse-usage-banner {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 16px;
      background: #17140a;
      border: 1px solid rgba(212, 184, 90, 0.35);
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
      color: #D4B85A;
      font-size: 13px;
    }

    .synapse-usage-banner i.pi-exclamation-triangle {
      font-size: 13px;
      flex-shrink: 0;
    }

    .synapse-usage-banner span {
      flex: 1;
      min-width: 0;
    }

    .synapse-usage-banner-action {
      flex-shrink: 0;
      color: inherit;
      text-decoration: underline;
      font-size: 12.5px;
      white-space: nowrap;
    }

    .synapse-usage-banner-dismiss {
      flex-shrink: 0;
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 2px 4px;
      opacity: 0.8;
    }

    .synapse-usage-banner-dismiss:hover {
      opacity: 1;
    }

    /* Aviso de revisión de ficheros (SYN-899): informativo (azul); en rojo si el procesado falló */
    .synapse-review-banner {
      background: #0d1420;
      border-color: rgba(168, 197, 240, 0.35);
      color: #A8C5F0;
    }

    .synapse-review-banner.is-error {
      background: #1d0f0f;
      border-color: rgba(232, 95, 92, 0.35);
      color: #E85F5C;
    }

    .synapse-review-banner i.pi:not(.pi-times) {
      font-size: 13px;
      flex-shrink: 0;
    }

    .synapse-review-banner button.synapse-usage-banner-action {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: inherit;
    }

    .synapse-review-toast {
      position: fixed;
      left: 50%;
      bottom: 28px;
      transform: translateX(-50%);
      z-index: 90;
      max-width: calc(100vw - 32px);
      padding: 10px 18px;
      border-radius: 9999px;
      background: #141416;
      border: 1px solid rgba(255, 255, 255, 0.14);
      color: #fff;
      font-size: 13px;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
    }
  `]
})
export class LayoutComponent implements OnInit, OnDestroy {
  readonly focusMode = inject(FocusModeService);
  private readonly nombreLocal = inject(NombreLocalService);
  /** Último usuario de Auth0, para rehacer el de la barra si cambia el nombre local. */
  private authUser: any = null;

  /**
   * El nombre sale del mismo sitio que en la lista de miembros y en el perfil
   * (`nombreVisible`), con el nombre puesto en Ajustes → Perfil por encima.
   */
  private buildSidebarUser(authUser: any): SidebarUser {
    const user = this.nombreLocal.aplicar(authUser);
    const nombre = nombreVisible(user);
    return {
      name: nombre,
      nickname: nombre,
      role: this.perm.role() || '',
      // La imagen genérica de Auth0 no es una foto: se usan las iniciales y el color
      // de Ajustes (shared/utils/avatar).
      avatar: avatarPicture(user.picture),
      initials: inicialesVisibles(user),
      avatarBackground: avatarGradientForSeed(user.email || nombre)
    };
  }
  isDarkMode = false;
  private readonly destroy$ = new Subject<void>();

  appIcon: SidebarAppIcon = {
    image: '/assets/img/Icono1_FondoNegro.svg',
    route: '/agent',
    title: 'Synapseguard'
  };

  currentUser: SidebarUser | null = null;

  sidebarSections: SidebarNavSection[] = [];
  sidebarNavItems: SidebarNavItem[] = [];
  isSidebarCollapsed = false;

  // ── Aviso global de cap de tokens superado (settings/usage) ──
  // Poll ligero (60s): el backend ya tiene su propio TTL anti-hammer de 30s
  // (SYNC_TTL_MS en langsmith.utils.js), así que no lo machacamos más rápido.
  private readonly tokenUsageService = inject(TokenUsageService);
  readonly usageVm = signal<UsageViewModel | null>(null);
  /** `resetAtLabel` del ciclo ya descartado por el usuario; null = nada descartado. */
  private readonly dismissedUsageCycle = signal<string | null>(null);
  readonly showUsageBanner = computed(() => {
    const vm = this.usageVm();
    if (!vm || !vm.exceeded) return false;
    return this.dismissedUsageCycle() !== vm.resetAtLabel;
  });

  // ── Revisión previa de ficheros de conocimiento (SYN-899) ──
  readonly knowledgeWatch = inject(KnowledgeReviewWatchService);
  readonly reviewBanner = this.knowledgeWatch.banner;
  /** Cuántos avisos más hay detrás del que se enseña. */
  readonly reviewBannerMore = computed(() => Math.max(0, this.knowledgeWatch.notices().length - 1));
  readonly reviewToast = signal<string | null>(null);
  private reviewToastTimer: ReturnType<typeof setTimeout> | null = null;

  openReviewFromBanner(n: ReviewNotice): void {
    if (n.file.reviewId) this.knowledgeWatch.openReview(n.file.reviewId);
  }

  /** La revisión terminó (confirmada, descartada, o confirmada y aún escribiéndose). */
  onReviewDone(outcome: ReviewOutcome): void {
    this.knowledgeWatch.closeReview();
    void this.knowledgeWatch.refresh();
    if (this.reviewToastTimer) clearTimeout(this.reviewToastTimer);
    this.reviewToast.set(this.translate.instant(`knowledge.review.toast.${outcome}`));
    this.reviewToastTimer = setTimeout(() => this.reviewToast.set(null), 5000);
  }

  constructor(
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly auth: AuthService,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    // Injecting GotifyService here ensures the WebSocket connection is
    // established as soon as the authenticated layout is rendered.
    private readonly gotify: GotifyService,
    // Injecting PushService registra el dispositivo en FCM/APNs y lo suscribe
    // al topic `alerts` para recibir push reales (app en segundo plano/cerrada).
    private readonly push: PushService,
    private readonly perm: PermissionService,
    // Store compartido del contador de alertas (app#600): el centro de alertas
    // empuja aquí sus mutaciones locales y el badge del sidebar responde al
    // instante, sin esperar al polling de 30s.
    private readonly alertsBadge: AlertsBadgeService
  ) {
    // Rebuild the sidebar whenever the user's permissions resolve/change so
    // nav items the role can't access are hidden.
    effect(() => {
      this.perm.ready();
      const role = this.perm.role();
      this.perm.grants();
      // El rol de la barra lateral es el resuelto desde Mongo, no un claim del token.
      if (this.currentUser) {
        this.currentUser = { ...this.currentUser, role: role || '' };
      }
      this.buildNavItems();
    });
    // El badge se pinta desde el store compartido: cualquier fuente (polling del
    // layout o mutación local del centro de alertas) que llame a set/applyDelta
    // se refleja sin recargar (app#600).
    // Al guardar el nombre en Ajustes → Perfil, la barra lateral lo pinta al momento.
    effect(() => {
      this.nombreLocal.version();
      if (this.authUser) {
        this.currentUser = this.buildSidebarUser(this.authUser);
      }
    });
    effect(() => {
      const c = this.alertsBadge.count();
      const item = this.sidebarSections.flatMap(s => s.items).find(i => i.route === '/alerts');
      if (item) item.count = c && c > 0 ? c : undefined;
    });
    // Lo mismo para el contador de revisiones pendientes, en la entrada /graph.
    effect(() => {
      const n = this.knowledgeWatch.count();
      const item = this.sidebarSections.flatMap(s => s.items).find(i => i.route === '/graph');
      if (item) item.count = n > 0 ? n : undefined;
    });
  }

  ngOnInit(): void {
    this.buildNavItems();

    // Suscribirse a los cambios de tema
    this.themeService.isDarkMode$.subscribe(isDark => {
      this.isDarkMode = isDark;
    });

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildNavItems();
    });

    // Suscribirse a los datos del usuario autenticado con mejor manejo de errores
    this.auth.user$.subscribe({
      next: (user) => {
        if (user) {
          this.authUser = user;
          this.currentUser = this.buildSidebarUser(user);
          // El workflow del agente (prod/dev) lo elige el usuario en la propia pantalla del
          // agente. Antes se leía aquí un claim `/agent` que ninguna Action de Auth0 emite
          // (venía de `user_metadata.agent`, dato muerto): se retira.

        } else {
          this.authUser = null;
          this.currentUser = null;
        }
      },
      error: (error) => {
        console.error('Error obteniendo datos del usuario:', error);
        this.authUser = null;
        this.currentUser = null;
      }
    });

    // Verificar estado de autenticación
    this.auth.isAuthenticated$.subscribe({
      next: (isAuthenticated) => {
        if (isAuthenticated) {
          // Iniciar polling de alertas solo cuando el usuario esté autenticado
          this.startAlertsPolling();
          this.startUsagePolling();
          // Una consulta a mis envíos: si hay una revisión lista o algo procesándose, aquí se entera.
          this.knowledgeWatch.start();
        } else {
          this.currentUser = null;
        }
      },
      error: (error) => {
        console.error('Error verificando autenticación:', error);
      }
    });


  }

  private buildNavItems(): void {
    // El conteo lo gobierna el store compartido (app#600); al reconstruir la nav
    // (idioma/permisos) arrancamos del valor vigente para no parpadear a vacío.
    const badgeCount = this.alertsBadge.count();
    const previousAlertsCount = badgeCount && badgeCount > 0
      ? badgeCount
      : this.sidebarSections.flatMap(s => s.items).find(item => item.route === '/alerts')?.count;

    this.sidebarSections = [
      {
        label: this.translate.instant('nav.section.panel'),
        items: [
          { icon: 'pi pi-home', route: '/metrics', label: this.translate.instant('nav.metrics') },
        ]
      },
      {
        label: this.translate.instant('nav.section.monitoring'),
        items: [
          { icon: 'pi pi-exclamation-triangle', route: '/alerts', label: this.translate.instant('nav.alerts'), count: previousAlertsCount },
          { icon: 'sg-ico sg-ico-eye', route: '/events', label: this.translate.instant('nav.events') },
          { icon: 'pi pi-users', route: '/identities', label: this.translate.instant('nav.identities') },
        ]
      },
      {
        label: this.translate.instant('nav.section.data'),
        items: [
          // El Conocimiento NO lleva icono propio: vive como pestaña dentro de /graph
          // (la barra de iconos en móvil no admite uno más — decisión 10-ago).
          // El contador son MIS revisiones pendientes (SYN-899): lo que espera a que lo confirmes.
          { icon: 'sg-ico sg-ico-hexagon-nodes', route: '/graph', label: this.translate.instant('nav.graph'), count: this.knowledgeWatch.count() || undefined },
          // Orígenes e Integraciones externas contiguas: las dos son «de dónde entran los datos».
          { icon: 'sg-ico sg-ico-arrows-to-dot', route: '/integrations', label: this.translate.instant('nav.origins') },
          { icon: 'pi pi-share-alt', route: '/third-party-integrations', label: this.translate.instant('nav.thirdPartyIntegrations') },
        ]
      }
    ];

    // Hide nav items the current role/grants can't access (preserving any
    // item already hidden by design).
    for (const item of this.sidebarSections.flatMap(s => s.items)) {
      if (item.route) {
        item.hidden = item.hidden || !this.perm.allows(ROUTE_PERMS[item.route]);
      }
    }

    this.sidebarNavItems = this.sidebarSections.flatMap(s => s.items);
  }

  onSidebarCollapsed(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
  }

  get isAuthenticated(): boolean {
    return !!this.currentUser;
  }



  private readonly GET_DATA_ALERTS_COUNT = gql`
    query Query {
      getDataAlertsCount
    }
  `;

  /**
   * Lanza la query de conteo de alertas. Los errores se absorben aquí (no se
   * propagan) para que un fallo puntual (p.ej. el token de Auth0 aún
   * calentando justo tras el redirect de login) no mate el polling de
   * startAlertsPolling() — con switchMap, un error sin capturar termina la
   * suscripción de interval() para siempre, y solo un refresco de página la
   * revive.
   */
  private queryAlertsCount() {
    return this.apollo.query<{ getDataAlertsCount: number }>({
      query: this.GET_DATA_ALERTS_COUNT,
      fetchPolicy: 'no-cache'
    }).pipe(
      // Reintenta rápido (cada 2s, hasta 3 veces) antes de rendirse — cubre el hipo
      // típico justo tras el login (token todavía no listo) sin esperar a los 30s
      // del siguiente ciclo de polling.
      retry({ count: 3, delay: 2000 }),
      catchError((error) => {
        console.error('Error obteniendo contador de alertas:', error);
        return of(null);
      })
    );
  }

  private applyAlertsCount(result: { data?: { getDataAlertsCount: number } } | null): void {
    if (!result) return; // error ya logueado; se conserva el último conteo válido
    // Valor autoritativo del servidor → store compartido; el effect pinta el badge
    // (app#600). El centro de alertas empuja además sus mutaciones locales.
    this.alertsBadge.set(result.data?.getDataAlertsCount ?? 0);
  }

  /**
   * Inicia el polling para actualizar el contador de alertas cada 30 segundos
   */
  private startAlertsPolling(): void {
    // Obtener el contador inicialmente
    this.queryAlertsCount().pipe(takeUntil(this.destroy$)).subscribe(result => this.applyAlertsCount(result));

    // Actualizar cada 30 segundos
    interval(30000).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.queryAlertsCount())
    ).subscribe(result => this.applyAlertsCount(result));
  }

  /**
   * Poll del contador de tokens para el banner global. Mismo patrón que
   * startAlertsPolling: carga inicial + intervalo, con error absorbido para no
   * matar la suscripción (interval + switchMap muere entero ante un error sin
   * capturar).
   */
  private startUsagePolling(): void {
    const poll = () => this.tokenUsageService.loadUsage().pipe(
      catchError(() => of(null)),
    );

    poll().pipe(takeUntil(this.destroy$)).subscribe(vm => { if (vm) this.usageVm.set(vm); });

    interval(60000).pipe(
      takeUntil(this.destroy$),
      switchMap(() => poll()),
    ).subscribe(vm => { if (vm) this.usageVm.set(vm); });
  }

  /** Descarta el banner para el ciclo vigente; reaparece si se abre uno nuevo. */
  dismissUsageBanner(): void {
    this.dismissedUsageCycle.set(this.usageVm()?.resetAtLabel ?? null);
  }

  ngOnDestroy(): void {
    if (this.reviewToastTimer) clearTimeout(this.reviewToastTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
